/**
 * 勘定科目別の予実(accountPl.ts)の読み込み(2026-10-23)。読むだけで書き込みはしない。
 *  - 取引: 月ごとに PL_QUERY_STATUSES × 計上月で読む(事業部を選んだときは事業部でも絞る)。
 *    使う索引は既存のもの(status+posting_month / department_id+status+posting_month)。実績・見込みは plEntryOf() で分ける
 *  - 予算: 年度ごとに読み、期間の月だけを使う
 */
import { collection, getDocs, query, where, type QueryDocumentSnapshot } from 'firebase/firestore';
import { getDb } from '@core/lib/firebase';
import { fiscalYearOf } from '@core/lib/date';
import { PL_QUERY_STATUSES, type Budget, type DepartmentId, type MonthStr, type Transaction } from '@core/lib/types';

function withId<T>(snap: QueryDocumentSnapshot): T {
  return { id: snap.id, ...(snap.data() as object) } as T;
}

/** departmentId が null なら全社 */
export async function fetchPlTransactionsForMonths(
  departmentId: DepartmentId | null,
  months: MonthStr[],
): Promise<Transaction[]> {
  const db = getDb();
  const lists = await Promise.all(
    months.map(async (month) => {
      const conditions = [where('status', 'in', PL_QUERY_STATUSES), where('posting_month', '==', month)];
      const q = departmentId
        ? query(collection(db, 'transactions'), where('department_id', '==', departmentId), ...conditions)
        : query(collection(db, 'transactions'), ...conditions);
      const snap = await getDocs(q);
      return snap.docs.map((d) => withId<Transaction>(d));
    }),
  );
  return lists.flat();
}

export async function fetchBudgetsForMonths(
  departmentId: DepartmentId | null,
  months: MonthStr[],
  startMonth: number,
): Promise<Budget[]> {
  const db = getDb();
  const years = [...new Set(months.map((m) => fiscalYearOf(`${m}-01`, startMonth)))];
  const monthSet = new Set(months);
  const lists = await Promise.all(
    years.map(async (fy) => {
      const q = departmentId
        ? query(collection(db, 'budgets'), where('department_id', '==', departmentId), where('fiscal_year', '==', fy))
        : query(collection(db, 'budgets'), where('fiscal_year', '==', fy));
      const snap = await getDocs(q);
      return snap.docs.map((d) => withId<Budget>(d));
    }),
  );
  return lists.flat().filter((b) => monthSet.has(b.month));
}
